/* eslint-disable @atlaskit/design-system/use-spotlight-package */
import Spotlight from '@atlaskit/onboarding/spotlight';
import SpotlightTransition from '@atlaskit/onboarding/spotlight-transition';
import useSpotlight from '@atlaskit/onboarding/use-spotlight';

import { useState } from 'react';

import { HomeSpotlight } from './HomeSpotlight';

import { RovoChatSpotlight } from './RovoChatSpotlight';

import { SwitcherSpotlight } from './SwitcherSpotlight';

import {
	FOR_YOU_TARGET,
	HOME_SWITCHER_TARGET,
	ROVO_CHAT_TARGET,
	SPOTLIGHT_WIDTH,
} from './constants';

import { SpotlightTourMode } from '../types';

import { type JSX } from 'react/jsx-runtime';

export type SpotlightTourProps = {
	userName?: string;
	openRovoPanel?: () => void; // if openRovoPanel function is provided, then rovo is enabled
	toggleSidebar?: () => void;
	dismiss: () => void;
	toggleSwitcher?: (toggle: 'open' | 'close') => void;
	isRovoChatOpen?: boolean;
	sidebarForcedOpen: boolean;
	spotlightTourMode: SpotlightTourMode;
	/**
	 * replaces the internal setTimeout function.
	 * Intended for testing purpose because timing is tricky in unit tests when using setTimeout
	 */
	_setTimeout?: (callback: () => void) => void;
};

const SpotlightTour = (props: SpotlightTourProps): JSX.Element | null => {
	const {
		userName,
		sidebarForcedOpen,
		openRovoPanel,
		toggleSidebar,
		toggleSwitcher,
		isRovoChatOpen,
		dismiss,
		spotlightTourMode,
	} = props;

	const setTimeoutToUse = props._setTimeout || setTimeout;
	const [activeSpotlight, setActiveSpotlight] = useState<null | number>(0);
	const { checkVisibility } = useSpotlight();

	const next = () => setActiveSpotlight((activeSpotlight || 0) + 1);
	const back = () => setActiveSpotlight((activeSpotlight || 1) - 1);

	const endTour = () => {
		setActiveSpotlight(null);
		dismiss();
	};

	const onOpenRovoPanel = () => {
		openRovoPanel && openRovoPanel();
		endTour();
	};

	const moveFromHomeToSwitcher = () => {
		if (sidebarForcedOpen) {
			toggleSidebar?.();
		}
		/**
		 * Delay the opening of switcher until the current execution stack has finished.
		 * This is to prevent the switcher being accidentally closed by something else
		 */
		setTimeoutToUse(() => {
			toggleSwitcher?.('open');
			// Wait for switcher to open first to avoid flickering
			setTimeoutToUse(() => {
				// Wait for switcher to open before moving on
				next();
			});
		});
	};

	const moveFromSwitcherToHome = () => {
		toggleSwitcher?.('close');
		setTimeoutToUse(() => {
			const isForYouTargetVisible = checkVisibility(FOR_YOU_TARGET)();
			if (!isForYouTargetVisible) {
				toggleSidebar?.();
			}
			// Wait for sidebar to open before moving on
			setTimeoutToUse(() => {
				back();
			});
		});
	};

	const moveFromSwitherToRovo = () => {
		toggleSwitcher?.('close');
		next();
	};

	const moveFromRovoToSwitcher = () => {
		/**
		 * Delay the opening of switcher until the current execution stack has finished.
		 * This is to prevent the switcher being accidentally closed by something else
		 */
		setTimeoutToUse(() => {
			toggleSwitcher?.('open');
			// Wait for switcher to open first to avoid flickering
			setTimeoutToUse(() => {
				// Wait for switcher to open before moving on
				back();
			});
		});
	};

	// if user does not have rovo enabled, then we show 2 step. Otherwise, we let TAP traits dictate how many steps we show.
	const numTotalSpotlights =
		spotlightTourMode === SpotlightTourMode.SHOW_ALL && !openRovoPanel
			? SpotlightTourMode.EXCLUDE_ROVO
			: spotlightTourMode;

	if (activeSpotlight === null) {
		return null;
	}

	const renderActiveSpotlight = () => {
		const spotlights = [
			<Spotlight
				target={FOR_YOU_TARGET}
				dialogWidth={SPOTLIGHT_WIDTH}
				dialogPlacement="right top"
				shouldWatchTarget
			>
				<HomeSpotlight
					userName={userName}
					spotlightEnd={endTour}
					spotlightNext={moveFromHomeToSwitcher}
					numTotalSpotlights={numTotalSpotlights}
				/>
			</Spotlight>,
			<Spotlight
				target={HOME_SWITCHER_TARGET}
				dialogWidth={SPOTLIGHT_WIDTH}
				dialogPlacement="right top"
				shouldWatchTarget
			>
				<SwitcherSpotlight
					spotlightEnd={endTour}
					spotlightNext={moveFromSwitherToRovo}
					spotlightBack={moveFromSwitcherToHome}
					numTotalSpotlights={numTotalSpotlights}
				/>
			</Spotlight>,
		];
		if (spotlightTourMode === SpotlightTourMode.SHOW_ALL) {
			spotlights.push(
				<Spotlight
					target={ROVO_CHAT_TARGET}
					dialogWidth={SPOTLIGHT_WIDTH}
					dialogPlacement="bottom right"
					shouldWatchTarget
				>
					<RovoChatSpotlight
						spotlightEnd={endTour}
						spotlightBack={moveFromRovoToSwitcher}
						openRovoPanel={onOpenRovoPanel}
						numTotalSpotlights={numTotalSpotlights}
						isRovoChatOpen={isRovoChatOpen ?? false}
					/>
				</Spotlight>,
			);
		}

		return spotlights[activeSpotlight];
	};

	return <SpotlightTransition>{renderActiveSpotlight()}</SpotlightTransition>;
};

export default SpotlightTour;
